import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { X } from "lucide-react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import {
  CONTACT_FIELDS,
  RfqCommercialNotes,
  RfqContactCapture,
  RfqErrorState,
  RfqHumanHandover,
  RfqNoticeState,
  RfqProgress,
  RfqQuestionRenderer,
  RfqServiceSelection,
  RfqSummary,
} from "@/features/rfq/RfqViews";
import {
  RFQ_STAGES,
  buildAttribution,
  calculateProgress,
  createInitialRfqState,
  normaliseAnswer,
  normaliseCommercialNote,
  rfqReducer,
} from "@/features/rfq/rfqMachine";

const API = "/gateway/api";
const EMPTY_FLOWS = Object.freeze({});
const CONFIG_ERROR = "Enquiry options are temporarily unavailable. Please try again shortly.";

export function extractEnquiryReference(data, headers = {}) {
  return [
    data?.enquiry_reference,
    data?.reference,
    headers?.["x-enquiry-reference"],
  ].find((value) => typeof value === "string" && value.trim() && !["undefined", "null"].includes(value.trim().toLowerCase()));
}

function isRecord(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function normaliseConfig(data) {
  return isRecord(data?.flows) ? data : { flows: EMPTY_FLOWS, load_error: CONFIG_ERROR };
}

async function requestConfigWithRetry(signal, onSuccess, onError) {
  for (let attempt = 0; attempt < 2; attempt += 1) {
    try {
      const { data } = await axios.get(`${API}/rfq/config`, { signal });
      onSuccess(normaliseConfig(data));
      return;
    } catch (error) {
      if (axios.isCancel(error) || signal?.aborted) return;
      if (attempt === 0) {
        await new Promise((resolve) => window.setTimeout(resolve, 300));
        if (signal?.aborted) return;
      }
    }
  }
  onError(CONFIG_ERROR);
}

function recordOrEmpty(value) {
  return isRecord(value) ? value : EMPTY_FLOWS;
}

function hasConfigFailure(config, configError) {
  return Boolean(config.load_error || configError);
}

function firstError(...messages) {
  return messages.find(Boolean) || "";
}
const SERVICE_LABELS = {
  warehousing: "Cold-Room Warehousing",
  transport: "Refrigerated Transport, Distribution and Multi-Drop Delivery",
  distribution: "Distribution and Multi-Drop Delivery",
  fulfilment: "E-Commerce Fulfilment",
  "cross-border": "Cross-Border Logistics",
  "4pl": "Integrated 4PL Logistics Solutions",
};
const SERVICE_OPTIONS = Object.entries(SERVICE_LABELS).filter(([service]) => service !== "distribution");
const SERVICE_OPENING = {
  warehousing: "We’ll start with your goods and required temperature, then prepare the details for our operations team.",
  transport: "Choose a single delivery or multi-drop distribution. We’ll ask only the questions needed for that movement.",
  fulfilment: "We’ll start with your products and sales channels, then prepare the fulfilment requirement for our team.",
  distribution: "We’ll start with the collection point and destinations, then prepare the distribution requirement for our team.",
  "cross-border": "We’ll start with the origin and destination countries, then prepare the movement details for our team.",
  "4pl": "We’ll begin by understanding the providers, locations and handovers you currently manage, then identify where one coordination layer could help.",
};

function trackAnalytics(event, details = {}) {
  window.dispatchEvent(new CustomEvent("ac-analytics", { detail: { event, ...details } }));
}

export function RfqAssistant({ open, onClose, initialService, context }) {
  const location = useLocation();
  const [config, setConfig] = useState({ flows: {} });
  const [configError, setConfigError] = useState("");
  const combinedInitialService = initialService === "distribution" ? "transport" : initialService;
  const [state, dispatch] = useReducer(rfqReducer, combinedInitialService, createInitialRfqState);
  const wasOpen = useRef(false);
  const closeButtonRef = useRef(null);
  const panelRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const loadConfig = useCallback(async (signal) => {
    setConfigError("");
    setConfig((current) => ({ ...current, load_error: "" }));
    await requestConfigWithRetry(signal, setConfig, setConfigError);
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    loadConfig(controller.signal);
    return () => controller.abort();
  }, [loadConfig]);

  useEffect(() => {
    if (open && !wasOpen.current) {
      dispatch({ type: "RESET", service: combinedInitialService || "" });
    }
    wasOpen.current = open;
  }, [open, combinedInitialService]);

  useEffect(() => {
    if (!open) return undefined;
    const previousOverflow = document.body.style.overflow;
    const previouslyFocused = document.activeElement;
    document.body.classList.add("rfq-open");
    document.body.style.overflow = "hidden";
    closeButtonRef.current?.focus();
    const handleKeyDown = (event) => {
      if (event.key === "Escape") onCloseRef.current();
      if (event.key !== "Tab" || !panelRef.current) return;
      const focusable = [...panelRef.current.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      )].filter((element) => !element.hidden && element.getAttribute("aria-hidden") !== "true");
      if (!focusable.length) {
        event.preventDefault();
        panelRef.current.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.body.classList.remove("rfq-open");
      document.removeEventListener("keydown", handleKeyDown);
      previouslyFocused?.focus?.();
    };
  }, [open]);

  const flows = recordOrEmpty(config?.flows);
  const answers = recordOrEmpty(state?.answers);
  const questions = useMemo(() => (flows[state.service] || []).filter((item) => {
    if (!item.show_when) return true;
    return Object.entries(item.show_when).every(([sourceId, accepted]) => accepted.includes(answers[sourceId]));
  }), [answers, flows, state.service]);
  const question = questions[state.step];
  const progress = useMemo(() => calculateProgress(state, questions.length), [state, questions.length]);
  const summaryRows = useMemo(() => questions.reduce((rows, item, step) => {
    if (!(item.id in state.answers)) return rows;
    rows.push({ id: item.id, label: item.label, value: state.answers[item.id], step });
    return rows;
  }, []).concat((() => {
    const cartonDimensions = String(state.answers.quantity || "").match(
      /^(\d+) Cartons · ([\d.]+) × ([\d.]+) × ([\d.]+) cm$/,
    );
    if (!cartonDimensions) return [];
    const [, quantity, length, width, height] = cartonDimensions.map(Number);
    const perCarton = (length * width * height) / 5000;
    const total = perCarton * quantity;
    const formatWeight = (value) => `${Number(value.toFixed(2))} kg`;
    return [{
      id: "estimated_volumetric_weight",
      label: "Indicative volumetric weight for preliminary review only. Final chargeable weight and pricing depend on the applicable service and carrier method.",
      value: `${formatWeight(perCarton)} per carton · ${formatWeight(total)} total`,
      step: questions.findIndex((item) => item.id === "quantity"),
    }];
  })()), [questions, state.answers]);

  const selectService = useCallback((service) => {
    dispatch({ type: "SELECT_SERVICE", service });
    trackAnalytics("quick_service_selected", { service });
  }, []);

  const answerQuestion = useCallback(async (rawAnswer) => {
    if (!question) return;
    const answer = normaliseAnswer(rawAnswer, question.optional);
    if (!answer) {
      dispatch({ type: "SET_ERROR", error: "Please add a detail before continuing." });
      return;
    }
    if (typeof answer === "string" && question.type === "text") {
      try {
        const { data } = await axios.post(`${API}/rfq/pricing-guard`, { message: answer, question_type: question.type });
        if (data.intercepted) {
          dispatch({
            type: "ADD_COMMERCIAL_COMMENT",
            note: {
              id: `${question.id}-${Date.now()}`,
              questionId: question.id,
              questionLabel: question.label,
              step: state.step,
              value: answer,
            },
            notice: data.response,
          });
          return;
        }
      } catch {
        dispatch({ type: "SET_ERROR", error: "We could not check that free-text answer safely. Please try again." });
        return;
      }
    }
    const prospectiveAnswers = { ...state.answers, [question.id]: answer };
    const prospectiveQuestions = (flows[state.service] || []).filter((item) => {
      if (!item.show_when) return true;
      return Object.entries(item.show_when).every(([sourceId, accepted]) => accepted.includes(prospectiveAnswers[sourceId]));
    });
    const prospectiveStep = prospectiveQuestions.findIndex((item) => item.id === question.id);
    const isLast = prospectiveStep + 1 >= prospectiveQuestions.length;
    dispatch({ type: "ANSWER", questionId: question.id, value: answer, isLast });
    if (isLast) trackAnalytics("key_requirement_completed", { service: state.service });
  }, [flows, question, state.answers, state.service, state.step]);

  const saveCommercialNote = useCallback((rawValue) => {
    try {
      const value = normaliseCommercialNote(rawValue);
      dispatch({ type: "SAVE_COMMERCIAL_NOTE", value });
    } catch (error) {
      dispatch({ type: "SET_ERROR", error: error.message || "Commercial note could not be saved." });
    }
  }, []);

  const setConsent = useCallback((value) => dispatch({ type: "SET_CONSENT", value }), []);

  const submit = useCallback(async (contact) => {
    const contactComplete = CONTACT_FIELDS.every(([key]) => contact[key]);
    if (!contactComplete || !state.consent) {
      dispatch({ type: "SET_ERROR", error: "Please complete the contact details and consent before sending." });
      return;
    }
    dispatch({ type: "SUBMIT" });
    try {
      const { data, headers } = await axios.post(`${API}/rfq/leads`, {
        service: state.service,
        answers: state.answers,
        commercial_comments: state.commercialComments.map((note) => note.value),
        contact,
        attribution: buildAttribution(location),
        uploaded_document_ids: [],
        upload_session_id: null,
        consent: state.consent,
        website: "",
      });
      const enquiryReference = extractEnquiryReference(data, headers);
      window.sessionStorage.setItem("enquiry_result", JSON.stringify(data || {}));
      if (enquiryReference) {
        window.sessionStorage.setItem("enquiry_reference", enquiryReference.trim());
      }
      trackAnalytics("enquiry_completed", { service: state.service });
      onClose();
      window.location.assign(enquiryReference
        ? `/enquiry-received?reference=${encodeURIComponent(enquiryReference.trim())}#reference=${encodeURIComponent(enquiryReference.trim())}`
        : "/enquiry-received");
    } catch (error) {
      dispatch({ type: "SET_ERROR", error: error.response?.data?.detail || "We could not send the requirement. Please try again." });
    }
  }, [location, onClose, state]);

  if (!open) return null;
  const activeError = firstError(state.error, config.load_error, configError);
  const suppliedContext = context && context !== "mobile" && !context.startsWith("/") ? context : "";
  return <div className="assistant-overlay" data-testid="rfq-assistant-overlay"><button className="assistant-dismiss" aria-label="Close enquiry panel" onClick={onClose} data-testid="rfq-assistant-backdrop"/><aside ref={panelRef} className="assistant-panel" role="dialog" aria-modal="true" aria-labelledby="rfq-assistant-title" data-testid="rfq-assistant" tabIndex="-1">
    <header><div><span className="assistant-kicker"><i aria-hidden="true"/> Guided enquiry</span><h2 id="rfq-assistant-title">Tell us what you need.</h2><p>A few practical details. No sales script.</p></div><button ref={closeButtonRef} onClick={onClose} aria-label="Close enquiry panel" data-testid="rfq-close-button"><X/></button></header>
    <RfqProgress progress={progress} serviceLabel={SERVICE_LABELS[state.service]} stage={state.stage} step={state.step} total={questions.length}/>
    <div className="assistant-body"><div className="assistant-note"><p>{SERVICE_OPENING[state.service] || suppliedContext || "Choose what you need help with. We’ll collect only the details our operations team needs to respond."}</p></div>
      {state.stage === RFQ_STAGES.IDLE && <RfqServiceSelection options={SERVICE_OPTIONS} onSelect={selectService}/>} 
      {state.stage === RFQ_STAGES.QUALIFICATION && <RfqQuestionRenderer question={question} value={state.value} addressSearchEnabled={Boolean(config.address_search_enabled)} onValueChange={(value) => dispatch({ type: "SET_VALUE", value })} onToggleMulti={(value) => dispatch({ type: "TOGGLE_MULTI", value })} onAnswer={answerQuestion}/>}
      {state.stage === RFQ_STAGES.COMMERCIAL_NOTES && <RfqCommercialNotes value={state.value} onContinue={saveCommercialNote}/>}
      {state.stage === RFQ_STAGES.REVIEW && <RfqSummary rows={summaryRows} commercialComments={state.commercialComments} onEdit={(step) => { const target = questions[step]; dispatch({ type: "EDIT", step, value: target ? state.answers[target.id] : "" }); }} onEditComment={(note) => dispatch({ type: "EDIT_COMMERCIAL_COMMENT", note })} onRemoveComment={(id) => dispatch({ type: "REMOVE_COMMERCIAL_COMMENT", id })} onAddComment={() => dispatch({ type: "SHOW_COMMERCIAL_NOTES" })} onContact={() => dispatch({ type: "SHOW_CONTACT" })} onHandover={() => dispatch({ type: "SHOW_HANDOVER" })}/>} {/* Human-reviewed commercial notes stay separate from operational answers. */}
      {state.stage === RFQ_STAGES.HUMAN_HANDOVER && <RfqHumanHandover onCallback={() => dispatch({ type: "SHOW_CONTACT" })}/>} 
      {[RFQ_STAGES.CONTACT, RFQ_STAGES.SUBMITTING].includes(state.stage) && <RfqContactCapture contact={state.contact} consent={state.consent} submitting={state.stage === RFQ_STAGES.SUBMITTING} onConsentChange={setConsent} onSubmit={submit}/>}
      <RfqErrorState message={activeError}/>
      {hasConfigFailure(config, configError) && <button type="button" className="secondary-button rfq-config-retry" onClick={() => loadConfig()} data-testid="rfq-config-retry">Retry enquiry options</button>}
      <RfqNoticeState message={state.notice}/>
    </div>
    {state.stage === RFQ_STAGES.QUALIFICATION && state.step > 0 && <footer><button onClick={() => { const target = questions[state.step - 1]; dispatch({ type: "BACK", value: target ? state.answers[target.id] : "" }); }} data-testid="rfq-back-button">Back</button><button onClick={() => dispatch({ type: "RESET" })} data-testid="rfq-change-service">Change service</button></footer>}
    {state.stage === RFQ_STAGES.COMMERCIAL_NOTES && <footer><button onClick={() => { const step = Math.max(0, questions.length - 1); const target = questions[step]; dispatch({ type: "BACK_FROM_COMMERCIAL_NOTES", step, value: target ? state.answers[target.id] : "" }); }} data-testid="rfq-back-button">Back</button><button onClick={() => dispatch({ type: "RESET" })} data-testid="rfq-change-service">Change service</button></footer>}
  </aside></div>;
}
